import { ConditionWrapper } from '@approvalmax/ui/src/components';
import { intl, numberHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning, WarningIndicator } from 'modules/request';
import { memo, useMemo } from 'react';
import bemFactory from 'react-bem-factory';

import { LineItemFooterLine } from '../../../../components/lineItemsSection/LineItemFooterLine/LineItemFooterLine';
import LineItemFooterSeparator from '../../../../components/lineItemsSection/LineItemFooterSeparator';
import { useGetRecognizedOcrAttachment } from '../../../../hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from '../../../../hooks/useOcrData';
import { getRequest } from '../../../../selectors/pageSelectors';
import {
    getQBooksAccountLineItemsSummary,
    getQBooksLineItemsSummary,
    mergeSummaries,
} from '../../../../selectors/qbooks';
import { isSalesInvoiceDetails } from '../../../../utils/typeGuards';
import { QBooksTaxSwitcherLegacy } from '../QBooksTaxSwitcherLegacy/QBooksTaxSwitcherLegacy';
import { messages } from './QBooksFooterLegacy.messages';
import {
    FooterLeft,
    FooterLines,
    FooterTotalLine,
    ReorderableContent,
    Root,
    StyledCurrencyDisclaimer,
} from './QBooksFooterLegacy.styles';

const qa = bemFactory.qa('line-items-section');

/**
 * @deprecated Use redesigned component {@link QBooksFooter}
 */
export const QBooksFooterLegacy = memo(() => {
    const request = useSelector<
        domain.QBooksPoRequest | domain.QBooksBillRequest | domain.QBooksExpenseRequest | domain.QBooksInvoiceRequest
    >(getRequest);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const isUSOrg = company.integration?.countryCode === backend.OrganisationVersion.QBO_US;
    const { details, currency, integrationCode } = request;
    const isSalesInvoice = integrationCode === domain.IntegrationCode.QBooksInvoice;
    const lineAmountType = details.lineAmountType;
    const discount = isSalesInvoiceDetails(details) && details.discount ? details.discount : null;
    const discountType = isSalesInvoiceDetails(details) ? details.discountType : null;
    const applyTaxAfterDiscount = isSalesInvoiceDetails(details) && details.applyTaxAfterDiscount;
    const shipping = isSalesInvoiceDetails(details)
        ? lineAmountType === domain.LineAmountType.TaxInclusive
            ? details.shipping.priceGross ?? null
            : details.shipping.price ?? null
        : null;

    const summary = useSelector((state) =>
        isSalesInvoice
            ? getQBooksLineItemsSummary(state, request)
            : mergeSummaries(
                  getQBooksLineItemsSummary(state, request),
                  getQBooksAccountLineItemsSummary(state, request)
              )
    );

    const taxComponents = useMemo(
        () =>
            isSalesInvoice || (!isSalesInvoice && lineAmountType !== domain.LineAmountType.NoTax)
                ? summary.taxComponents.filter(({ hidden }) => !hidden)
                : [],
        [isSalesInvoice, lineAmountType, summary.taxComponents]
    );

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const showSubtotalWarning = Boolean(
        ocrData?.document?.subTotal && summary.subtotalAmount !== ocrData?.document?.subTotal
    );

    const totalTax = useMemo(
        () =>
            summary.taxComponents.reduce((prev, curr) => {
                return prev + curr.taxAmount;
            }, 0),
        [summary.taxComponents]
    );

    const showTaxTotalWarning =
        numberHelpers.isNumber(ocrData?.document?.totalTax) && totalTax !== ocrData?.document?.totalTax;
    const showTotalWarning = Boolean(ocrData?.document?.total && summary.totalAmount !== ocrData?.document?.total);

    return (
        <Root alignItems='start' spacing='0 16'>
            <FooterLeft>
                <StyledCurrencyDisclaimer />
            </FooterLeft>

            {isSalesInvoice && <QBooksTaxSwitcherLegacy />}

            <FooterLines>
                <WarningIndicator
                    show={showSubtotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={summary.subtotalAmount}
                            value2={ocrData?.document?.subTotal}
                            label1={messages.differentSubtotalWarningLabel1}
                            label2={messages.differentSubtotalWarningLabel2}
                            description={messages.differentSubtotalWarningDescription}
                        />
                    }
                >
                    <LineItemFooterLine
                        qa={qa('subtotal-footer-line')}
                        warning={showSubtotalWarning}
                        label={messages.subtotalText({ currency })}
                    >
                        {intl.formatNumber(summary.subtotalAmount, 2)}
                    </LineItemFooterLine>
                </WarningIndicator>

                <ReorderableContent
                    direction='column'
                    spacing='0'
                    alignItems='end'
                    $ordered={isUSOrg}
                    $applyTaxAfterDiscount={applyTaxAfterDiscount}
                >
                    {isSalesInvoice && (
                        <>
                            {!!summary.discountAmount && (
                                <LineItemFooterLine
                                    label={
                                        discountType === domain.QBooksDiscountType.Percent
                                            ? messages.discountTextPercent({ discount })
                                            : messages.discountText
                                    }
                                >
                                    {intl.formatNumber(-summary.discountAmount, 2)}
                                </LineItemFooterLine>
                            )}

                            {!!shipping && (
                                <LineItemFooterLine label={messages.shippingText}>
                                    {intl.formatNumber(shipping, 2)}
                                </LineItemFooterLine>
                            )}

                            {/* TODO: SalesInvoice: Implement after the backend is ready */}
                            {/* <LineItemFooterLine label={messages.taxOnShippingText}> */}
                            {/*    {intl.formatNumber(0, 2)} */}
                            {/* </LineItemFooterLine> */}
                        </>
                    )}

                    {taxComponents.map(({ taxPercent, taxAmount, taxableAmount }, index) => (
                        <ConditionWrapper
                            condition={index === taxComponents.length - 1}
                            key={index}
                            wrapperIf={(children) => (
                                <WarningIndicator
                                    show={showTaxTotalWarning}
                                    content={
                                        <TwoDifferentValuesWarning
                                            value1={totalTax}
                                            value2={ocrData?.document?.totalTax}
                                            label1={messages.differentTaxTotalWarningLabel1}
                                            label2={messages.differentTaxTotalWarningLabel2}
                                            description={messages.differentTaxTotalWarningDescription}
                                        />
                                    }
                                >
                                    {children}
                                </WarningIndicator>
                            )}
                        >
                            <LineItemFooterLine
                                qa={qa('tax-component-footer-line')}
                                warning={index === taxComponents.length - 1 && showTaxTotalWarning}
                                label={
                                    lineAmountType === domain.LineAmountType.TaxExclusive || isUSOrg
                                        ? messages.taxLineTextExclusive({
                                              percent: taxPercent,
                                              taxableAmount: intl.formatNumber(taxableAmount, 2),
                                          })
                                        : messages.taxLineTextInclusive({
                                              percent: taxPercent,
                                              taxableAmount: intl.formatNumber(taxableAmount, 2),
                                          })
                                }
                            >
                                {intl.formatNumber(taxAmount, 2)}
                            </LineItemFooterLine>
                        </ConditionWrapper>
                    ))}
                </ReorderableContent>

                <LineItemFooterSeparator />

                {summary.nonDefaultCurrency && (
                    <LineItemFooterLine label={messages.totalText({ currency: summary.defaultCurrency })}>
                        <FooterTotalLine data-qa={qa('total-default-currency-footer-line')}>
                            {intl.formatNumber(summary.totalInDefaultCurrency, 2)}
                        </FooterTotalLine>
                    </LineItemFooterLine>
                )}

                <WarningIndicator
                    show={showTotalWarning}
                    content={
                        <TwoDifferentValuesWarning
                            value1={summary.totalAmount}
                            value2={ocrData?.document?.total}
                            label1={messages.differentTotalWarningLabel1}
                            label2={messages.differentTotalWarningLabel2}
                            description={messages.differentTotalWarningDescription}
                        />
                    }
                >
                    <LineItemFooterLine warning={showTotalWarning} label={messages.totalText({ currency })}>
                        <FooterTotalLine data-qa={qa('total-footer-line')}>
                            {intl.formatNumber(summary.totalAmount, 2)}
                        </FooterTotalLine>
                    </LineItemFooterLine>
                </WarningIndicator>
            </FooterLines>
        </Root>
    );
});

QBooksFooterLegacy.displayName = 'QBooksFooter';
